import { ChangeEvent, useMemo } from 'react';
import { useTheme } from '@/theme/ThemeProvider';
import { useLocation } from '@/providers/LocationProvider';
import { themeOrder } from '@/theme/themeProfiles';
import styles from './ThemeSwitcher.module.css';

const selectId = 'theme-switcher-select';

export const ThemeSwitcher = () => {
  const {
    themeType,
    setThemeType,
    availableThemes,
    locationEnabled,
    setLocationEnabled,
    autoStatus,
    autoPalette,
  } = useTheme();
  const location = useLocation();

  const handleThemeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextTheme = event.target.value as (typeof themeOrder)[number];
    setThemeType(nextTheme);
  };

  const handleModeToggle = (event: ChangeEvent<HTMLInputElement>) => {
    setLocationEnabled(event.target.checked);
  };

  const statusMessage = useMemo(() => {
    if (!locationEnabled) {
      return 'Using saved theme';
    }

    if (location.status === 'denied') {
      return 'Location disabled — using default theme';
    }
    if (location.status === 'unsupported') {
      return 'Location unavailable — using default theme';
    }
    if (location.status === 'error') {
      return 'Location error — using stored theme';
    }
    if (autoStatus === 'loading') {
      return 'Fetching campus/area palette…';
    }
    if (autoStatus === 'error') {
      return 'Palette unavailable — using Standard theme';
    }
    if (autoStatus === 'ready' && autoPalette) {
      const paletteLabel = autoPalette.name ?? autoPalette.paletteId ?? 'location-based';
      return `Using ${paletteLabel} palette for your campus/area`;
    }
    return 'Location-based theming active';
  }, [autoPalette, autoStatus, location.status, locationEnabled]);

  return (
    <section className={styles.wrapper}>
      <div className={styles.modeRow}>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={locationEnabled}
            onChange={handleModeToggle}
            aria-describedby="theme-mode-status"
          />
          <span>Allow location based themes</span>
        </label>
        <span id="theme-mode-status" className={styles.status} aria-live="polite">
          {statusMessage}
        </span>
      </div>
      <div className={styles.controlGroup}>
        <label className={styles.label} htmlFor={selectId}>
          Accessibility presets
        </label>
        <div className={styles.control}>
          <select id={selectId} value={themeType} onChange={handleThemeChange}>
            {availableThemes.map((theme) => (
              <option key={theme.id} value={theme.id}>
                {theme.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </section>
  );
};
